import StorageService from "../services/StorageService.js";
import MapInteraction from "../utils/MapInteraction.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import BaseGame from "./BaseGame.js";

/** Temps laissé au joueur pour voir l'État révélé après une erreur, en ms. */
const REVEAL_DELAY = 1500;

/**
 * US States, mode Classic : carte des USA, on donne le nom (+ drapeau) d'un
 * État, il faut cliquer dessus. Copie de MapNerdClassic.js sans la logique de
 * scope/continent (un seul territoire, tous les États sont toujours jouables).
 */
export default class UsStatesClassic extends BaseGame {
	constructor() {
		super("usstatesclassic", ".us-states-classic", {
			gameNameKey: "home.usStates",
			modeLabelKey: "home.classic",
			mode: "classic",
			shareEmoji: "🟦",
			slug: "us-states-classic"
		});
		if (!this.page) return;

		this.mapContainer = this.page.querySelector(".map-container");
		this.map = this.mapContainer.querySelector("svg");
		this.countryToGuess = this.page.querySelector(".country-to-guess");
		this.countryToGuessName = this.countryToGuess.querySelector(".name");
		this.countryToGuessFlag = this.countryToGuess.querySelector(".flag-container");
		this.countries = this.map.querySelectorAll("path");

		this.mapInteraction = new MapInteraction(this.mapContainer, this.map);
		this.buildClickEvent();
		this.infiniteButton();
		this.init();

		this.isInfinite = false;
	}

	init() {
		this.initCountries(geoNerdApp.usStates);
		this.startClock();
		this.newProposal();
		this.reloadButton();
		this.bindRestartButton();
		this.initShare();
		this.updateHeartsUI();
		this.updateProgress();
		this.maybeShowHelp();
	}

	initCountries(defaultList) {
		super.initCountries(defaultList);
		geoNerdApp.usStates.forEach(s => {
			const path = this.map.querySelector(`#${s.code}`);
			if (!path) return;
			path.classList.add("onu");
			if (!this.countriesLeft.find(left => left.code === s.code)) {
				path.classList.add("guessed");
			}
		});
	}

	/** Popup "Comment jouer ?" une fois par visite, comme MapNerdController.maybeShowHelp(). */
	maybeShowHelp() {
		const hideHelpKey = `geonerd.${this.gameId}.hideHelp`;
		if (localStorage.getItem(hideHelpKey) === "true") return;
		const popupOverlay = this.page.querySelector(".help-popup-overlay");
		if (!popupOverlay) return;

		const description = popupOverlay.querySelector(".help-description");
		description.innerHTML = geoNerdApp.languageManager.t("game.usStatesClassicHelp");
		popupOverlay.classList.add("show");

		const closeBtn = popupOverlay.querySelector(".btn-close-help");
		const checkbox = popupOverlay.querySelector("#dont-show-help");
		closeBtn.addEventListener("click", () => {
			if (checkbox && checkbox.checked) localStorage.setItem(hideHelpKey, "true");
			popupOverlay.classList.remove("show");
		}, { once: true });
	}

	buildClickEvent() {
		this.countries.forEach(country => {
			country.addEventListener("click", () => {
				if (!this.mapInteraction.dragged && this.canGuess && country.classList.contains("onu") && !country.classList.contains("guessed")) {
					this.guessOnMap(country.id);
				}
			});
		});
	}

	newProposal() {
		if (this.destroyed) return;
		if (this.countriesLeft.length === 0) {
			this.finishGame();
			return;
		}

		gsap.to(this.countryToGuess, { opacity: 1 });
		this.countryToGuess.classList.remove("valid", "invalid");

		let current = StorageService.load(`${this.gameId}.current`);
		if (!current || current.found) {
			this.rightAnswer = this.countriesLeft[Math.floor(Math.random() * this.countriesLeft.length)];
			StorageService.save(`${this.gameId}.current`, this.rightAnswer);
		} else {
			this.rightAnswer = current;
			const match = geoNerdApp.usStates.find(c => c.code === this.rightAnswer.code);
			if (match) this.rightAnswer.name = match.name;
		}

		this.countryToGuessName.innerHTML = this.rightAnswer.name;
		this.countryToGuessFlag.innerHTML = `<div class="flag" style="background-image: url(${getFlagPath(`us-${this.rightAnswer.code}`)})"></div>`;
	}

	guessOnMap(countryCode) {
		if (this.destroyed) return;
		this.canGuess = false;
		const isCorrect = countryCode === this.rightAnswer.code;

		if (!isCorrect) {
			this.recordError({
				countryName: this.rightAnswer.name,
				correctCode: this.rightAnswer.code,
				guessedCode: countryCode
			});
		}

		if (isCorrect) {
			SoundManager.correct();
			FeedbackEffects.confetti(this.countryToGuess);
			this.countryToGuess.classList.add("valid");
			const path = this.map.querySelector(`#${countryCode}`);
			if (path) path.classList.add("guessed");

			this.countriesLeft = this.countriesLeft.filter(elem => elem.code !== this.rightAnswer.code);
			StorageService.save(`${this.gameId}.current`, { found: true });
			this.updateStorage();
			this.updateProgress();
		} else {
			SoundManager.wrong();
			FeedbackEffects.shake(this.countryToGuess);
			this.countryToGuess.classList.add("invalid");
			const path = this.map.querySelector(`#${countryCode}`);
			if (path) {
				path.classList.add("invalid-flash");
				setTimeout(() => path.classList.remove("invalid-flash"), 500);
			}
			this.revealCorrectCountry();
			// Sans ça, `newProposal()` retombait sur ce même État (le `current`
			// persisté n'est marqué trouvé qu'en cas de bonne réponse) : une erreur
			// forçait à retenter l'État raté au lieu d'en tirer un autre au sort.
			StorageService.remove(`${this.gameId}.current`);
		}

		const delay = isCorrect ? 600 : REVEAL_DELAY;

		if (this.isInfinite || isCorrect) {
			setTimeout(() => {
				this.newProposal();
				this.canGuess = true;
			}, delay);
		} else {
			this.updateLife(true);
			if (this.lifes > 0) {
				setTimeout(() => {
					this.newProposal();
					this.canGuess = true;
				}, delay);
			}
		}
	}

	/** Montre l'État cherché après une erreur (voir MapNerdClassic.revealCorrectCountry). */
	revealCorrectCountry() {
		const path = this.map.querySelector(`#${this.rightAnswer.code}`);
		if (!path) return;
		path.classList.add("revealed-answer");
		setTimeout(() => path.classList.remove("revealed-answer"), REVEAL_DELAY - 100);
	}

	updateLife(decrease) {
		if (this.isInfinite) {
			this.updateProgress();
			return;
		}

		if (decrease) {
			this.lifes--;
			StorageService.save(`${this.gameId}.lifes`, this.lifes);
		}

		this.updateHeartsUI();

		if (this.lifes <= 0) {
			SoundManager.gameOver();
			this.lifes = 0;
			StorageService.save(`${this.gameId}.lifes`, 0);
			this.recordLoss();

			const correctPath = this.map.querySelector(`#${this.rightAnswer.code}`);
			if (correctPath) {
				correctPath.classList.add("correct-answer");
				this.mapInteraction.zoomToElement(correctPath, null, 2);
			}

			this.revealRecapAfterDelay(() => {
				this.renderEndGameStats(this.looseMessage);
				this.renderErrors(this.looseMessage);
				this.looseMessage.classList.add("show");
				this.page.querySelector(".progress-container").style.display = "none";
				if (correctPath) correctPath.classList.remove("correct-answer");
			});
		}
		this.updateProgress();
	}

	/**
	 * Défère `reveal` de 2 s, le temps de voir où était l'État sur la carte
	 * avant de passer au récap. Le bouton "Recommencer" disparaît dans l'instant :
	 * laissé visible, il restait cliquable pendant ce délai et réinitialisait la
	 * partie au lieu de montrer le récap. La sauvegarde (best/stats) a déjà eu
	 * lieu au moment de cet appel.
	 */
	revealRecapAfterDelay(reveal) {
		const restartBtn = this.page.querySelector(".btn-restart-ingame");
		if (restartBtn) restartBtn.style.display = "none";
		setTimeout(reveal, 2000);
	}

	infiniteButton() {
		const btn = this.page.querySelector(".btn.infinite");
		const quitBtn = this.page.querySelector(".btn.infinite-quit");

		if (btn) {
			btn.addEventListener("click", () => {
				this.isInfinite = true;
				this.looseMessage.classList.remove("show");
				this.page.querySelector(".life-remaining").style.display = "none";
				this.page.querySelector(".btn-restart-ingame").style.display = "none";
				this.page.querySelector(".progress-container").style.display = "flex";
				this.page.querySelector(".progress-container").style.pointerEvents = "all";
				this.page.querySelector(".infinite-actions").style.display = "flex";

				if (!this.page.querySelector(".infinite-label-added")) {
					this.page.querySelector(".progress").insertAdjacentHTML("beforeend", `<span class="infinite-label-added">&nbsp;-&nbsp;${window.geoNerdApp.languageManager.t('game.infiniteMode')}</span>`);
				}

				this.newProposal();
				this.canGuess = true;
			});
		}

		if (quitBtn) {
			quitBtn.addEventListener("click", () => {
				window.location.href = window.geoNerdApp.languageManager.currentLang === "en" ? "/en/" : "/";
			});
		}
	}

	renderErrorItem(error) {
		const guessed = geoNerdApp.usStates.find(s => s.code === error.guessedCode);
		return `
			<div class="error-item">
				<div class="flags-duel">
					<div class="error-flag wrong">
						<img src="${getFlagPath(`us-${error.guessedCode}`)}" alt="${guessed?.name ?? ""}">
						<div class="error-answer-text wrong">${guessed?.name ?? ""}</div>
					</div>
					<div class="error-flag correct">
						<img src="${getFlagPath(`us-${error.correctCode}`)}" alt="${error.countryName}">
						<div class="error-answer-text correct">${error.countryName}</div>
					</div>
				</div>
			</div>
		`;
	}

	finishGame() {
		SoundManager.victory();
		this.mapInteraction.destroy();

		if (!this.isInfinite) this.recordWin();
		this.updateProgress();

		this.revealRecapAfterDelay(() => {
			this.mapContainer.remove();
			this.countryToGuess.remove();
			if (this.isInfinite) {
				this.renderEndGameStats(this.winMessage);
				this.winMessage.classList.add("show");
				if (this.progressContainer) this.progressContainer.style.display = "none";
			} else {
				this.showWinMessage();
			}
		});
	}

	teardown() {
		this.destroyed = true;
		this.canGuess = false;
		if (this.mapInteraction) this.mapInteraction.destroy();
	}
}
